import { useCallback, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { createMailFolder } from "@/lib/api/imap";
import { flattenFoldersForPicker } from "@/lib/folders/folderTree";
import { MailModalOverlay } from "@/features/shared/MailModalOverlay";
import type { MailFolder } from "@/lib/types/mail";

type Props = {
  open: boolean;
  accountId: number;
  folders: MailFolder[];
  /** Pré-remplit le nom (ex. « Attente » pour le slot snooze). */
  defaultName?: string;
  /** Parent IMAP suggéré (ex. INBOX). */
  defaultParent?: string | null;
  title?: string;
  onClose: () => void;
  onCreated: (folder: MailFolder) => void;
};

export function MailCreateFolderDialog({
  open,
  accountId,
  folders,
  defaultName = "",
  defaultParent = null,
  title = "Nouveau dossier",
  onClose,
  onCreated,
}: Props) {
  const flat = useMemo(() => flattenFoldersForPicker(folders), [folders]);
  const [name, setName] = useState(defaultName);
  const [parentFolder, setParentFolder] = useState(defaultParent ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setName(defaultName);
    setParentFolder(defaultParent ?? "");
    setError(null);
  }, [defaultName, defaultParent]);

  const handleClose = useCallback(() => {
    if (busy) return;
    reset();
    onClose();
  }, [busy, onClose, reset]);

  const handleSubmit = useCallback(async () => {
    const trimmed = name.trim();
    if (!trimmed || busy) return;

    setBusy(true);
    setError(null);
    try {
      const res = await createMailFolder({
        accountId,
        name: trimmed,
        parentFolder: parentFolder.trim() || undefined,
      });
      if (!res.ok) {
        setError(res.message);
        return;
      }
      onCreated(res.data.folder);
      reset();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Création impossible.");
    } finally {
      setBusy(false);
    }
  }, [accountId, busy, name, onClose, onCreated, parentFolder, reset]);

  return (
    <MailModalOverlay open={open} onClose={handleClose} labelledBy="mail-create-folder-title">
      <div className="px-5 py-5">
        <h2 id="mail-create-folder-title" className="text-base font-semibold text-[var(--mail-text)]">
          {title}
        </h2>
        <div className="mt-4 space-y-4">
        <p className="text-xs leading-relaxed text-[var(--mail-muted)]">
          Le dossier est créé sur le serveur IMAP — visible dans Aurélia, la webmail et les autres
          clients (Thunderbird, MailBird…).
        </p>

        <label className="block space-y-1.5">
          <span className="text-xs font-medium text-[var(--mail-text)]">Nom du dossier</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex. Projets, Attente, Clients…"
            className="w-full rounded-lg border border-[var(--mail-border-soft)] bg-[var(--mail-surface)] px-3 py-2 text-sm"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") void handleSubmit();
            }}
          />
        </label>

        <label className="block space-y-1.5">
          <span className="text-xs font-medium text-[var(--mail-text)]">Dossier parent (optionnel)</span>
          <select
            value={parentFolder}
            onChange={(e) => setParentFolder(e.target.value)}
            className="w-full rounded-lg border border-[var(--mail-border-soft)] bg-[var(--mail-surface)] px-3 py-2 text-sm"
          >
            <option value="">Racine du compte</option>
            {flat.map((f) => (
              <option key={f.name} value={f.name}>
                {f.label}
              </option>
            ))}
          </select>
        </label>

        {error ? (
          <p className="text-xs text-red-600" role="alert">
            {error}
          </p>
        ) : null}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={handleClose}
            disabled={busy}
            className="rounded-lg border border-[var(--mail-border-soft)] px-3 py-2 text-xs font-medium disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={busy || !name.trim()}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--mail-accent)] px-3 py-2 text-xs font-semibold text-white disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
            Créer
          </button>
        </div>
        </div>
      </div>
    </MailModalOverlay>
  );
}
